import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  Receipt,
  Search,
  Filter,
  Download,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Calendar,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import {
  useChecks,
  useCheckStats,
  type CheckFilters,
  type CheckTransaction,
  getDaysUntilExpiration,
  getExpirationStatus,
  getCheckStatusDisplay,
} from '@/hooks/queries/useChecks';
import { useState } from 'react';
import { CheckDetailModal } from '@/features/treasury/components/CheckDetailModal';

export const Route = createFileRoute('/apps/treasury/checks/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: ChecksPage,
});

function ChecksPage() {
  const navigate = useNavigate();
  const { company } = useAuthStore();
  
  const [filters, setFilters] = useState<CheckFilters>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCheck, setSelectedCheck] = useState<CheckTransaction | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const { data: checks = [], isLoading, refetch } = useChecks(company?.id || '', {
    ...filters,
    search: searchQuery || undefined,
  });
  
  const stats = useCheckStats(company?.id || '');

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate({ to: '/apps/treasury' })}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">
              الشيكات / Chèques
            </h1>
            <p className="mt-2 text-gray-600">
              تتبع جميع الشيكات مع قاعدة 180 يوماً / Suivez tous vos chèques avec la règle des 180 jours
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => refetch()}
            >
              تحديث / Actualiser
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                // TODO: Export to Excel
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              Exporter
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Total Checks */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Receipt className="w-8 h-8 text-gray-600" />
                <span className="text-2xl font-bold">{stats.total}</span>
              </div>
              <p className="text-sm text-gray-600">Total chèques</p>
            </CardContent>
          </Card>

          {/* Pending */}
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilters({ status: 'pending' })}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Clock className="w-8 h-8 text-yellow-600" />
                <span className="text-2xl font-bold text-yellow-600">{stats.byStatus.pending}</span>
              </div>
              <p className="text-sm text-gray-600">En attente</p>
            </CardContent>
          </Card>

          {/* Deposited */}
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilters({ status: 'deposited' })}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-8 h-8 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">{stats.byStatus.deposited}</span>
              </div>
              <p className="text-sm text-gray-600">Déposés</p>
            </CardContent>
          </Card>

          {/* Cleared */}
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilters({ status: 'cleared' })}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="w-8 h-8 text-green-600" />
                <span className="text-2xl font-bold text-green-600">{stats.byStatus.cleared}</span>
              </div>
              <p className="text-sm text-gray-600">Encaissés</p>
            </CardContent>
          </Card>

          {/* Bounced */}
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilters({ status: 'bounced' })}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <XCircle className="w-8 h-8 text-red-600" />
                <span className="text-2xl font-bold text-red-600">{stats.byStatus.bounced}</span>
              </div>
              <p className="text-sm text-gray-600">Rejetés</p>
            </CardContent>
          </Card>
        </div>

        {/* Expiring Checks Alert */}
        {stats.expiring > 0 && (
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-orange-600" />
                <div>
                  <p className="font-medium text-orange-900">
                    {stats.expiring} chèque(s) expirent bientôt
                  </p>
                  <p className="text-sm text-orange-700">
                    Les chèques sont valables 180 jours en Algérie
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-auto border-orange-300"
                  onClick={() => setFilters({ status: 'pending' })}
                >
                  Voir
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters & Search */}
        <Card>
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filtres et recherche
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="N° chèque, tireur..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Status Filter */}
              <Select
                value={filters.status || 'all'}
                onValueChange={(value) =>
                  setFilters({ ...filters, status: value === 'all' ? undefined : value as any })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="deposited">Déposés</SelectItem>
                  <SelectItem value="cleared">Encaissés</SelectItem>
                  <SelectItem value="bounced">Rejetés</SelectItem>
                  <SelectItem value="cancelled">Annulés</SelectItem>
                </SelectContent>
              </Select>

              {/* Type Filter */}
              <Select
                value={filters.type || 'all'}
                onValueChange={(value) =>
                  setFilters({ ...filters, type: value === 'all' ? undefined : value as any })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="received">Reçus (clients)</SelectItem>
                  <SelectItem value="issued">Émis (fournisseurs)</SelectItem>
                </SelectContent>
              </Select>

              {/* Clear Filters */}
              {(filters.status || filters.type || searchQuery) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setFilters({});
                    setSearchQuery('');
                  }}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Effacer
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Checks Table */}
        <Card>
          <CardHeader className="border-b">
            <CardTitle>
              {checks.length} chèque(s)
              {filters.status && ` - ${getCheckStatusDisplay(filters.status).label}`}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Chargement...</p>
              </div>
            ) : checks.length === 0 ? (
              <div className="text-center py-12">
                <Receipt className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Aucun chèque trouvé
                </h3>
                <p className="text-gray-600">
                  {filters.status || filters.type || searchQuery
                    ? 'Aucun chèque ne correspond à vos filtres'
                    : 'Vous n\'avez pas encore de chèques enregistrés'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        N° Chèque
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Tireur / Bénéficiaire
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Banque
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Montant
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Expiration
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {checks.map((check) => {
                      const statusDisplay = getCheckStatusDisplay(check.check_status);
                      const expirationStatus = getExpirationStatus(check.check_date);
                      const daysRemaining = getDaysUntilExpiration(check.check_date);
                      const showExpiration = check.check_status === 'pending' || check.check_status === 'deposited';

                      return (
                        <tr key={check.id} className="hover:bg-gray-50 transition-colors">
                          {/* Check Number */}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-medium text-gray-900">
                                {check.check_number}
                              </span>
                            </div>
                          </td>

                          {/* Drawer/Beneficiary */}
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-gray-900">{check.check_drawer_name}</p>
                              <p className="text-sm text-gray-500 line-clamp-1">{check.description}</p>
                            </div>
                          </td>

                          {/* Bank */}
                          <td className="px-6 py-4">
                            <p className="text-sm text-gray-900">{check.check_bank || '-'}</p>
                          </td>

                          {/* Date */}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              <p className="text-sm text-gray-900">{formatDate(check.check_date)}</p>
                            </div>
                          </td>

                          {/* Amount */}
                          <td className="px-6 py-4">
                            <p className={`text-sm font-semibold ${
                              check.type === 'income' ? 'text-green-600' : 'text-gray-900'
                            }`}>
                              {formatCurrency(check.amount)}
                            </p>
                          </td>

                          {/* Type */}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1">
                              {check.type === 'income' ? (
                                <>
                                  <TrendingDown className="w-4 h-4 text-green-600" />
                                  <span className="text-sm text-gray-600">Reçu</span>
                                </>
                              ) : (
                                <>
                                  <TrendingUp className="w-4 h-4 text-gray-600" />
                                  <span className="text-sm text-gray-600">Émis</span>
                                </>
                              )}
                            </div>
                          </td>

                          {/* Status */}
                          <td className="px-6 py-4">
                            <Badge className={`${statusDisplay.bgColor} ${statusDisplay.color}`}>
                              {statusDisplay.icon} {statusDisplay.label}
                            </Badge>
                          </td>

                          {/* Expiration */}
                          <td className="px-6 py-4">
                            {showExpiration ? (
                              <Badge className={`${expirationStatus.bgColor} ${expirationStatus.color}`}>
                                {daysRemaining < 0 ? (
                                  'Expiré'
                                ) : (
                                  `${daysRemaining}j`
                                )}
                              </Badge>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>

                          {/* Actions */}
                          <td className="px-6 py-4 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedCheck(check);
                                setShowDetailModal(true);
                              }}
                            >
                              Voir
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Check Detail Modal */}
        <CheckDetailModal
          open={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedCheck(null);
            refetch();
          }}
          check={selectedCheck}
        />
      </div>
    </DashboardLayout>
  );
}
